import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getInvitationByToken, acceptInvitation, type Invitation } from "../services/invitations";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../lib/dateFormat";

export default function InviteAccept() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    password: "",
    confirmPassword: "",
  });

  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link");
      setLoading(false);
      return;
    }

    loadInvitation();
  }, [token]);

  async function loadInvitation() {
    if (!token) return;

    try {
      setLoading(true);
      const data = await getInvitationByToken(token);
      setInvitation(data);
      setError(null);
    } catch (err: any) {
      console.error("Failed to load invitation:", err);
      if (err?.response?.status === 410) {
        setError(err?.response?.data?.error || "Invitation is no longer valid");
      } else if (err?.response?.status === 404) {
        setError("Invitation not found");
      } else {
        setError("Failed to load invitation");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept(e: React.FormEvent) {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert(t("invite.passwordsNotMatch", "Passwords do not match"));
      return;
    }

    if (formData.password.length < 6) {
      alert(t("invite.passwordTooShort", "Password must be at least 6 characters"));
      return;
    }

    if (!token) return;

    setAccepting(true);
    try {
      const result = await acceptInvitation(token, {
        password: formData.password,
        full_name: formData.full_name.trim() || undefined,
      });

      // Show success state
      setSuccess(true);

      // Existing account: the role was attached to it — no new session is
      // issued, so send the user to sign in with their existing password.
      if (result.existing_account || !result.token || !result.user) {
        setTimeout(() => {
          // A parent still has to create the child; carry the invitation
          // through the sign-in so they land on add-child, not a dashboard
          // that shows them nothing.
          navigate(
            result.next === "add_child" && result.invitation_token
              ? `/login?next=${encodeURIComponent(`/app/parent/add-child?invitation=${result.invitation_token}`)}`
              : "/login"
          );
        }, 1800);
        return;
      }

      // New account: set auth using the context (stores token + refreshes user).
      await setAuth(
        {
          id: result.user.id,
          email: result.user.email,
          name: result.user.name,
          full_name: result.user.name,
        },
        result.token
      );

      // Small delay to show success animation, then redirect.
      // A parent's account is only half the job: the child does not exist yet,
      // and the invitation is what knows which club and team it belongs to.
      setTimeout(() => {
        navigate(
          result.next === "add_child" && result.invitation_token
            ? `/app/parent/add-child?invitation=${result.invitation_token}`
            : "/dashboard"
        );
      }, 1500);
    } catch (err: any) {
      console.error("Failed to accept invitation:", err);
      alert(err?.response?.data?.error || t("invite.acceptError", "Failed to accept invitation"));
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-night-900 flex items-center justify-center">
        <div className="text-ink-100 text-xl">{t("common.loading", "Loading...")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-night-900 flex items-center justify-center p-6">
        <div className="bg-glass-900 border border-glass-700 rounded-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-red-400 mb-4">
            {t("invite.invalidInvite", "Invalid Invitation")}
          </h1>
          <p className="text-ink-300 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-colors"
          >
            {t("invite.goHome", "Go to Homepage")}
          </button>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-night-900 flex items-center justify-center p-6">
        <div className="bg-glass-900 border border-glass-700 rounded-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4 animate-bounce">🎉</div>
          <h1 className="text-3xl font-bold text-brand-500 mb-4">
            {t("invite.welcomeSuccess", "Welcome to iGROWth!")}
          </h1>
          <p className="text-ink-300 mb-6">
            {t("invite.accountCreated", "Your account has been created successfully. Redirecting you to the dashboard...")}
          </p>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-night-900 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-glass-900 border border-glass-700 rounded-lg p-5 sm:p-8 max-w-md w-full">
        {/* Invitation Header */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">✉️</div>
          <h1 className="text-3xl font-bold text-brand-500 mb-2">
            {t("invite.youAreInvited", "You're Invited!")}
          </h1>
          <p className="text-ink-300">
            {t("invite.joinPlatform", "Join iGROWth platform and start your journey")}
          </p>
        </div>

        {/* Invitation Details */}
        <div className="bg-night-900 border border-glass-700 rounded-lg p-4 mb-6 min-w-0 overflow-hidden">
          <div className="space-y-3 min-w-0">
            <div>
              <span className="text-sm text-ink-400">{t("invite.invitedBy", "Invited by")}:</span>
              <p className="text-ink-100 font-medium break-words">
                {invitation.invited_by?.full_name || t("invite.clubAdmin", "Club Administrator")}
              </p>
            </div>

            {invitation.club && (
              <div>
                <span className="text-sm text-ink-400">{t("invite.club", "Club")}:</span>
                <p className="text-ink-100 font-medium break-words">{invitation.club.name}</p>
              </div>
            )}

            {invitation.team && (
              <div>
                <span className="text-sm text-ink-400">{t("invite.team", "Team")}:</span>
                <p className="text-ink-100 font-medium break-words">
                  {invitation.team.name}
                  {invitation.team.age_group && (
                    <span className="text-sm text-ink-400 ml-2">({invitation.team.age_group})</span>
                  )}
                </p>
              </div>
            )}

            {invitation.role_code === "parent" && invitation.athlete_name && (
              <div>
                <span className="text-sm text-ink-400">{t("invite.for_child", "Pentru")}:</span>
                <p className="text-ink-100 font-medium break-words">{invitation.athlete_name}</p>
              </div>
            )}

            <div>
              <span className="text-sm text-ink-400">{t("invite.role", "Role")}:</span>
              <p className="text-ink-100 font-medium capitalize">
                {invitation.role_code === "parent"
                  ? t("invite.role_parent", "Părinte")
                  : invitation.role_code}
              </p>
            </div>

            {invitation.role_code === "parent" && (
              <div className="px-3 py-2.5 text-sm rounded-xl bg-brand-500/10 border border-brand-500/30 text-ink-200">
                {t(
                  "invite.parent_next",
                  "După ce îți creezi contul, adaugi contul copilului. Intră automat în clubul și echipa de mai sus."
                )}
              </div>
            )}

            <div>
              <span className="text-sm text-ink-400">{t("invite.email", "Email")}:</span>
              <p className="text-ink-100 font-medium break-all">{invitation.email}</p>
            </div>

            <div>
              <span className="text-sm text-ink-400">{t("invite.expires", "Expires")}:</span>
              <p className="text-ink-100 font-medium">
                {formatDate(new Date(invitation.expires_at))}
              </p>
            </div>
          </div>
        </div>

        {/* Acceptance Form */}
        <form onSubmit={handleAccept} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ink-200 mb-2">
              {t("invite.fullName", "Full Name")}
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder={t("invite.fullNamePlaceholder", "Enter your full name")}
              className="w-full px-4 py-3 bg-night-900 border border-glass-700 rounded-lg text-ink-100 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-200 mb-2">
              {t("invite.password", "Password")} *
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={t("invite.passwordPlaceholder", "Choose a strong password")}
              className="w-full px-4 py-3 bg-night-900 border border-glass-700 rounded-lg text-ink-100 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <p className="text-xs text-ink-400 mt-1">
              {t("invite.passwordHint", "Minimum 6 characters")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-200 mb-2">
              {t("invite.confirmPassword", "Confirm Password")} *
            </label>
            <input
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder={t("invite.confirmPasswordPlaceholder", "Re-enter your password")}
              className="w-full px-4 py-3 bg-night-900 border border-glass-700 rounded-lg text-ink-100 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          <button
            type="submit"
            disabled={accepting}
            className="w-full px-6 py-3 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-600 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
          >
            {accepting
              ? t("invite.accepting", "Creating Account...")
              : t("invite.acceptAndJoin", "Accept Invitation & Create Account")}
          </button>
        </form>

        {/* Footer Note */}
        <p className="text-center text-xs text-ink-400 mt-6">
          {t(
            "invite.acceptanceNote",
            "By accepting this invitation, you agree to create an account and join the specified club/team"
          )}
        </p>
      </div>
    </div>
  );
}
